import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Menus, Text, useTheme } from '@Sage/hoi-poi-ui';

import ConfirmModal from 'components/ConfirmModal';
import HtmlPreviewFrame from 'containers/components/HtmlPreviewFrame';
import { applyFixedFooterPreviewToHtml } from 'containers/campaigns/marketing/EmailEditor/fixedFooter';
import CampaignPreviewTestModal from 'containers/campaigns/marketing/MarketingCampaignsForm/CampaignPreviewTestModal';
import {
    createMarketingEmailTemplate,
    deleteMarketingEmailTemplate,
} from 'services/CampaignsService';
import { getLiteral } from 'utils/getLiteral';
import { errorToast, successToast } from 'utils/toast';

/**
 * API row shape (v2 templates).
 * @typedef {{ id?: number|string, name?: string, strBody?: string, strJson?: string }} MarketingTemplateRow
 */

const bodyToPreviewHtml = (strBody) => {
    const raw = typeof strBody === 'string' ? strBody.trim() : '';
    if (!raw) {
        return '<p></p>';
    }
    const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(raw);
    return looksLikeHtml ? raw : `<p>${raw}</p>`;
};

/**
 * Whether the template JSON looks like Topol config (aligned with MarketingTemplatesCreate).
 * @param {MarketingTemplateRow} template
 * @returns {boolean}
 */
function isTopolTemplate(template) {
    const jsonRaw = template?.strJson;
    const jsonStr = typeof jsonRaw === 'string' ? jsonRaw.trim() : '';
    return jsonStr.startsWith('{') || jsonStr.startsWith('[');
}

/**
 * Predefined template card with hover actions, optional overflow menu, and preview modal.
 * Overflow menu is only shown in the templates library list (`showActionsMenu`).
 */
const PredefinedTemplateCard = memo(
    ({
        template,
        footerPreview,
        showActionsMenu = false,
        onUseTemplate,
        onOpenHtmlEditor,
        onOpenVisualEditor,
        onTemplateLibraryChanged,
    }) => {
        const theme = useTheme();
        const [isHovered, setIsHovered] = useState(false);
        const [isPreviewOpen, setIsPreviewOpen] = useState(false);
        const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
        const [isDeleteLoading, setIsDeleteLoading] = useState(false);
        const duplicateInFlightRef = useRef(false);

        const name = template?.name?.trim?.() || getLiteral('label_email_template');
        const previewHtml = useMemo(
            () =>
                applyFixedFooterPreviewToHtml(bodyToPreviewHtml(template?.strBody), footerPreview),
            [footerPreview, template?.strBody],
        );

        const handleUseTemplate = useCallback(() => {
            onUseTemplate?.(template);
        }, [onUseTemplate, template]);

        const handleOpenPreview = useCallback((event) => {
            event?.stopPropagation?.();
            setIsPreviewOpen(true);
        }, []);

        const handleClosePreview = useCallback(() => {
            setIsPreviewOpen(false);
        }, []);

        const handleEdit = useCallback(() => {
            if (isTopolTemplate(template)) {
                onOpenVisualEditor?.({
                    strJson: template?.strJson,
                    initialTitle: template?.name ?? '',
                    templateId: template?.id,
                });
                return;
            }
            onOpenHtmlEditor?.({
                initialHtml: template?.strBody ?? '',
                initialTitle: template?.name ?? '',
                templateId: template?.id,
            });
        }, [onOpenHtmlEditor, onOpenVisualEditor, template]);

        const handlePreviewFromMenu = useCallback(() => {
            setIsPreviewOpen(true);
        }, []);

        const handleDuplicate = useCallback(async () => {
            if (duplicateInFlightRef.current) {
                return;
            }
            duplicateInFlightRef.current = true;
            const baseName = (template?.name || '').trim() || getLiteral('label_email_template');
            const duplicateName = `${baseName}-copy`;

            const payload = {
                name: duplicateName,
                html: template?.strBody ?? '',
            };
            if (isTopolTemplate(template)) {
                payload.topolConfig = template.strJson;
            }

            try {
                await createMarketingEmailTemplate(payload);
                successToast({
                    title: getLiteral('title_success_template_created'),
                    text: getLiteral('label_success_template_created'),
                });
                onTemplateLibraryChanged?.();
            } catch {
                errorToast({
                    title: getLiteral('title_error_template_created'),
                    text: getLiteral('label_error_template_created'),
                });
            } finally {
                duplicateInFlightRef.current = false;
            }
        }, [onTemplateLibraryChanged, template]);

        const handleCreateCampaign = useCallback(() => {
            onUseTemplate?.(template);
        }, [onUseTemplate, template]);

        const openDeleteModal = useCallback(() => {
            setIsDeleteModalOpen(true);
        }, []);

        const closeDeleteModal = useCallback(() => {
            if (!isDeleteLoading) {
                setIsDeleteModalOpen(false);
            }
        }, [isDeleteLoading]);

        const handleConfirmDelete = useCallback(async () => {
            const id = template?.id;
            if (id === null || id === undefined || id === '') {
                errorToast({ text: getLiteral('error_generalerror') });
                return;
            }
            setIsDeleteLoading(true);
            try {
                await deleteMarketingEmailTemplate(id);
                setIsDeleteModalOpen(false);
                onTemplateLibraryChanged?.();
            } catch {
                errorToast({ text: getLiteral('error_generalerror') });
            } finally {
                setIsDeleteLoading(false);
            }
        }, [onTemplateLibraryChanged, template?.id]);

        const menuItems = useMemo(
            () => [
                {
                    title: <Text type="body">{getLiteral('action_edit')}</Text>,
                    icon: <Icon name="edit" color={theme.colors.actionMinor[500]} />,
                    onClick: handleEdit,
                },
                {
                    title: <Text type="body">{getLiteral('action_preview')}</Text>,
                    icon: <Icon name="visibility" color={theme.colors.actionMinor[500]} />,
                    onClick: handlePreviewFromMenu,
                },
                {
                    title: <Text type="body">{getLiteral('action_duplicate')}</Text>,
                    icon: <Icon name="contentCopy" color={theme.colors.actionMinor[500]} />,
                    onClick: handleDuplicate,
                },
                { divider: true },
                {
                    title: <Text type="body">{getLiteral('action_create_campaign')}</Text>,
                    icon: <Icon name="emailSendOutline" color={theme.colors.actionMinor[500]} />,
                    onClick: handleCreateCampaign,
                },
                { divider: true },
                {
                    title: (
                        <Text type="body" color={theme.colors.semantic.negative500}>
                            {getLiteral('action_delete')}
                        </Text>
                    ),
                    icon: <Icon name="deleteRaw" color={theme.colors.semantic.negative500} />,
                    onClick: openDeleteModal,
                },
            ],
            [
                theme.colors.actionMinor,
                theme.colors.semantic.negative500,
                handleCreateCampaign,
                handleDuplicate,
                handleEdit,
                handlePreviewFromMenu,
                openDeleteModal,
            ],
        );

        return (
            <>
                <div
                    className={
                        showActionsMenu
                            ? 'marketing-templates-predefined-card'
                            : 'marketing-templates-predefined-card marketing-templates-predefined-card--selection'
                    }
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    <div className="marketing-templates-predefined-card__thumb">
                        <HtmlPreviewFrame
                            className="marketing-templates-predefined-card__iframe"
                            html={previewHtml}
                            title={name}
                            scale={0.3}
                        />
                        {isHovered && (
                            <div className="marketing-templates-predefined-card__overlay">
                                <Button onClick={handleUseTemplate}>
                                    {getLiteral(
                                        showActionsMenu
                                            ? 'action_create_campaign'
                                            : 'label_marketing_campaigns_templates_use_template',
                                    )}
                                </Button>
                                <Button
                                    type="secondary"
                                    className="marketing-templates-predefined-card__preview-btn"
                                    onClick={handleOpenPreview}
                                    icon="visibility"
                                >
                                    {getLiteral('action_preview')}
                                </Button>
                            </div>
                        )}
                    </div>
                    <div className="marketing-templates-predefined-card__footer">
                        <Text
                            as="div"
                            type="caption"
                            medium
                            className="marketing-templates-predefined-card__title"
                            isTruncated
                            useTooltip
                        >
                            {name}
                        </Text>
                        {showActionsMenu && (
                            <div
                                className="marketing-templates-predefined-card__menu-wrap"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                <Menus
                                    items={menuItems}
                                    placement="bottom-end"
                                    size="medium"
                                    title={
                                        <button
                                            type="button"
                                            className="marketing-templates-predefined-card__menu-trigger"
                                            aria-haspopup="true"
                                            aria-label={getLiteral('label_action')}
                                        >
                                            <Icon name="moreVert" color={theme.colors.grey[500]} />
                                        </button>
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>

                <CampaignPreviewTestModal
                    isOpen={isPreviewOpen}
                    onRequestClose={handleClosePreview}
                    from=""
                    subject={name}
                    previewText=""
                    htmlPreview={previewHtml}
                    defaultRecipient=""
                />

                <ConfirmModal
                    isOpen={isDeleteModalOpen}
                    title={getLiteral('label_marketing_campaigns_templates_delete_title')}
                    confirmText={getLiteral('action_delete')}
                    cancelText={getLiteral('action_cancel')}
                    onClose={closeDeleteModal}
                    onConfirm={handleConfirmDelete}
                    isLoading={isDeleteLoading}
                    buttonType="primary-error"
                >
                    <Text type="body">
                        {getLiteral('label_marketing_campaigns_templates_delete_desc')}
                    </Text>
                </ConfirmModal>
            </>
        );
    },
);

PredefinedTemplateCard.displayName = 'PredefinedTemplateCard';

PredefinedTemplateCard.propTypes = {
    showActionsMenu: PropTypes.bool,
    footerPreview: PropTypes.shape({
        senderName: PropTypes.string,
        senderAddress: PropTypes.string,
        footerHere: PropTypes.string,
        linkHref: PropTypes.string,
    }),
    onOpenHtmlEditor: PropTypes.func,
    onOpenVisualEditor: PropTypes.func,
    onTemplateLibraryChanged: PropTypes.func,
    onUseTemplate: PropTypes.func,
    template: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        strBody: PropTypes.string,
        strJson: PropTypes.string,
    }).isRequired,
};

export default PredefinedTemplateCard;
